import { Clock, ArrowRight } from 'lucide-react';
import { Input } from '@/shared/ui/Input';
import { cn } from '@/shared/lib/utils';

export interface AppointmentTimeWindowProps {
  startValue: string;
  endValue: string;
  rangeLabel: string;
  durationMinutes: number;
  startDisabled?: boolean;
  endDisabled?: boolean;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  className?: string;
}

/**
 * Emphasized appointment window — start/end editors with a clear range summary.
 * Intended to sit below slot selection as the final time confirmation step.
 */
export function AppointmentTimeWindow({
  startValue,
  endValue,
  rangeLabel,
  durationMinutes,
  startDisabled,
  endDisabled,
  onStartChange,
  onEndChange,
  className,
}: AppointmentTimeWindowProps): JSX.Element {
  const hasWindow = Boolean(startValue && endValue);

  return (
    <div
      className={cn(
        'apt-time-window',
        hasWindow ? 'apt-time-window--active' : 'apt-time-window--idle',
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'mt-0.5 flex size-10 flex-none items-center justify-center rounded-xl',
              hasWindow ? 'bg-brand-500 text-white shadow-soft' : 'bg-surface-100 text-ink-400',
            )}
          >
            <Clock className="size-5" aria-hidden />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-ink-900">Appointment window</div>
            <p className="mt-0.5 text-[11px] leading-relaxed text-ink-500">
              {hasWindow
                ? 'Confirm or fine-tune start and end. Slot selection fills these automatically.'
                : 'Pick a slot above, or enter start and end times manually.'}
            </p>
          </div>
        </div>
        {hasWindow ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-brand-500 px-3 py-1 text-[12px] font-semibold tabular-nums text-white shadow-soft">
              {rangeLabel}
            </span>
            <span className="rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-brand-700 ring-1 ring-brand-200">
              {durationMinutes} min
            </span>
          </div>
        ) : (
          <span className="rounded-full bg-surface-100 px-2.5 py-1 text-[11px] font-medium text-ink-500">
            Not set
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
        <div>
          <label className="form-label" htmlFor="apt-start-time">
            Start time
          </label>
          <Input
            id="apt-start-time"
            type="time"
            className={cn(
              'h-11 w-full font-mono text-[14px] font-semibold tabular-nums',
              hasWindow && 'border-brand-300 bg-white',
            )}
            value={startValue}
            onChange={(e) => onStartChange(e.target.value)}
            disabled={startDisabled}
          />
        </div>

        <div className="hidden pb-2.5 sm:flex sm:justify-center" aria-hidden>
          <div
            className={cn(
              'flex size-9 items-center justify-center rounded-full',
              hasWindow ? 'bg-brand-100 text-brand-600' : 'bg-surface-100 text-ink-400',
            )}
          >
            <ArrowRight className="size-4" />
          </div>
        </div>

        <div>
          <label className="form-label" htmlFor="apt-end-time">
            End time
          </label>
          <Input
            id="apt-end-time"
            type="time"
            className={cn(
              'h-11 w-full font-mono text-[14px] font-semibold tabular-nums',
              hasWindow && 'border-brand-300 bg-white',
            )}
            value={endValue}
            onChange={(e) => onEndChange(e.target.value)}
            disabled={endDisabled}
          />
        </div>
      </div>
    </div>
  );
}
